function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { runAgent, waitAgent, runIdOf } from "./agent-run";
import { AssetPicker } from "./sequence-create";
import { loadBrandKit } from "../../functions/brand-kit";
import { TrimTimeline, CutTimeline, SafeZoneLayer, grabFrames, captureFrame } from "./clip-timeline";
import endpoints from "../../endpoints";
import { call, toast } from "../audiences/api";
import { fmtClock as fmt } from "./sequences-api";
import { hasBothApps, studioUrl } from "./images-api";
import { MusicTracks } from "./music-tracks";

/*
 * Clip editor (2026-10-08 redesign, approved mockup PlunkDocs mockups/video-editors-redesign-2026-10-08.html, screens 1-2).
 * Same look as the image workspace redesign: top bar · the player on a quiet stage · timeline card with trim handles ·
 * prompt bar to extend · versions filmstrip · side panel with Edit / Sizes / Details tabs, tools open in place.
 *
 * No backend of its own. Every call is one the previous clip workspace (and video-edit.jsx) already made:
 *   trim and Find moments  content_video_trim_agent  → POST /content/assets/{id}/edits action "trim"
 *   extend                 content_video_extend_agent → POST edits action "extend_video" (8 s per run; + 16 s = two runs)
 *   cut and caption        content_video_edit_agent   → POST edits action "video_edit" (one row per output)
 *   find moments           content_clip_agent (proposals) + GET /content/assets/{id}/transcript
 *   sizes                  content_video_resize_agent → POST edits action "video_resize"
 *   cover image            POST /content/assets/upload-url, PUT the frame, POST /content/assets/{id}/poster (the Make step's thumbnail picker)
 *   re-direct              ClipForm (clip-workspace.jsx) with the recipe from the clip_render edit row
 * Works for any video; nothing here assumes a product type. No credit amounts; the only credit copy is the
 * insufficient-credits notice with the purchase path.
 */

const ROOT = String(endpoints.product && endpoints.product.root || "");
const CONTENT = `${ROOT}/content`;
const BUY_URL = "https://account.plunk.io/?from=app";
const FRAMES = 10;
const EXTEND_SECONDS = 8;
const enc = encodeURIComponent;
const idStr = v => v === undefined || v === null ? "" : String(v);
const num = (v, d) => {
  const n = parseFloat(v);
  return isNaN(n) ? d : n;
};
const uuid = () => window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : String(Date.now());
async function safe(p, fb) {
  try {
    const r = await p;
    return r === undefined || r === null ? fb : r;
  } catch (e) {
    return fb;
  }
}
const VIDEO_TARGETS = [["story", "Reels / Stories 9:16", 1080, 1920], ["square", "Instagram 1:1", 1080, 1080], ["portrait", "Feed 4:5", 1080, 1350], ["wide", "YouTube 16:9", 1920, 1080]];
const ASPECTS = [["", "Keep source"], ["16:9", "Wide 16:9"], ["9:16", "Tall 9:16"], ["1:1", "Square 1:1"], ["4:5", "Portrait 4:5"]];
const TARGETS = [["social", "Social (Reels, Stories)"], ["ads", "Ads"], ["product page", "Product page"], ["email", "Email"]];
const TILES = [["trim", "content_cut", "Trim"], ["cut", "movie_edit", "Cut and caption"], ["moments", "auto_awesome", "Find moments"], ["extend", "east", "Extend"], ["redirect", "videocam", "Re-direct"], ["cover", "image", "Cover image"]];
/** Side panel header per tool: a plain name and one sentence. Works for any video. */
const TOOL_INFO = {
  trim: ["Trim", "Keep one part of the clip. Drag the ends of the timeline, or type the times here."],
  cut: ["Cut and caption", "Keep the best moments, put them in order and add words on screen."],
  moments: ["Find moments", "Plunk reads the scene changes and anything said, then suggests short clips to make."],
  extend: ["Extend", "Add generated seconds after the last frame, matched to the look of the clip."],
  redirect: ["Re-direct", "Change the shots, length or format and render again from the same starting image."],
  cover: ["Cover image", "Pick the frame people see before the video plays. It shows in Assets and wherever the video is used."]
};
const CUT0 = {
  segments: [],
  sel: 0,
  captions: true,
  aspect: "",
  musicOn: false,
  music: "",
  endCard: false,
  endTitle: "",
  endSub: "",
  separate: false,
  transcript: null,
  brand: {},
  dirty: false,
  loading: false
};
const SIZES0 = {
  safe: null,
  drawing: false,
  fill: "blur",
  fillColor: "#000000",
  picked: new Set(["story", "square"]),
  results: null
};

/** Version label used across the editor: "Original" for the first, vN after. */
const vlabel = x => x && x.version > 1 ? `v${x.version}` : "Original";

/** Where an editor's Back goes: a same-app ?back= path under /Content (set when opened from a piece), else Assets. Read
 *  once when the editor opens, so switching versions (which rewrites the address) keeps it. */
export function editorBack() {
  try {
    const b = new URLSearchParams(window.location.search).get("back");
    if (b && /^\/Content(?:[/?]|$)/.test(b) && !/^\/\//.test(b)) return b;
  } catch (e) {/* no window */}
  return "";
}
export default class ClipEditor extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "_back", editorBack());
    _defineProperty(this, "state", {
      tab: "edit",
      tool: "",
      busy: "",
      time: 0,
      dur: 0,
      playing: false,
      natural: null,
      trim: {
        start: 0,
        end: 0
      },
      frames: null,
      extPrompt: "",
      extSteps: 1,
      credits: null,
      moreOpen: false,
      cut: CUT0,
      cutPlay: false,
      cutIdx: 0,
      moments: {
        clips: undefined,
        picked: new Set(),
        target: "social",
        transcript: null
      },
      sizes: SIZES0,
      cover: {
        mode: "frame",
        alsoRoot: true,
        imageId: "",
        assets: [],
        tainted: false
      }
    });
    _defineProperty(this, "vid", /*#__PURE__*/React.createRef());
    _defineProperty(this, "more", /*#__PURE__*/React.createRef());
    _defineProperty(this, "_alive", true);
    _defineProperty(this, "_raf", 0);
    _defineProperty(this, "_trimGuard", false);
    _defineProperty(this, "onDocDown", e => {
      if (this.state.moreOpen && this.more.current && !this.more.current.contains(e.target)) this.setState({
        moreOpen: false
      });
    });
    _defineProperty(this, "set", patch => {
      if (this._alive) this.setState(patch);
    });
    _defineProperty(this, "loadFrames", () => {
      const url = this.asset().url;
      if (!url) return;
      grabFrames(url, FRAMES).then(fr => {
        if (this._alive && this.asset().url === url) this.setState({
          frames: fr
        });
      }).catch(() => {});
    });
    /* ---------- the player ---------- */
    _defineProperty(this, "onMeta", e => {
      const v = e.target;
      const d = isFinite(v.duration) && v.duration > 0 ? v.duration : this.durOf();
      const patch = {
        dur: d,
        natural: v.videoWidth && v.videoHeight ? {
          w: v.videoWidth,
          h: v.videoHeight
        } : null
      };
      const t = this.state.trim;
      if (!t.end || t.end > d || t.start === 0 && Math.abs(t.end - (Number(this.asset().durationSeconds) || 0)) < 0.6) patch.trim = {
        start: Math.min(t.start, d),
        end: d
      };
      const c = this.state.cut;
      if (c.segments.length === 1 && !(Number(c.segments[0].end) > 0)) patch.cut = {
        ...c,
        segments: [{
          ...c.segments[0],
          end: Math.round(d * 10) / 10
        }]
      };
      this.setState(patch);
    });
    _defineProperty(this, "onPlay", () => {
      this.setState({
        playing: true
      });
      cancelAnimationFrame(this._raf);
      this._raf = requestAnimationFrame(this.tick);
      clearTimeout(this._recover);
      this._recover = setTimeout(() => {
        const el = this.vid.current;
        if (!el || el.paused || el.ended || el.currentTime > 0.05 || this._recovered) return;
        this._recovered = true;
        el.load();
        el.play().catch(() => {});
      }, 1200);
    });
    _defineProperty(this, "onPause", () => {
      cancelAnimationFrame(this._raf);
      const v = this.vid.current;
      this.setState({
        playing: false,
        cutPlay: false,
        time: v ? v.currentTime : this.state.time
      });
    });
    _defineProperty(this, "tick", () => {
      const v = this.vid.current;
      if (!v || !this._alive) return;
      const t = v.currentTime;
      const s = this.state;
      if (s.cutPlay) {
        const segs = s.cut.segments;
        const seg = segs[s.cutIdx];
        if (!seg) {
          v.pause();
          return;
        }
        if (t >= Number(seg.end) - 0.04 || t < Number(seg.start) - 0.5) {
          const next = s.cutIdx + 1;
          if (next < segs.length) {
            v.currentTime = Number(segs[next].start);
            this.setState({
              cutIdx: next,
              cut: {
                ...s.cut,
                sel: next
              }
            });
          } else {
            v.pause();
            this.setState({
              cutPlay: false
            });
            return;
          }
        }
      } else if (this._trimGuard && t >= s.trim.end - 0.03) {
        this._trimGuard = false;
        v.pause();
      }
      if (Math.abs(t - s.time) > 0.06) this.setState({
        time: t
      });
      this._raf = requestAnimationFrame(this.tick);
    });
    _defineProperty(this, "seek", t => {
      const v = this.vid.current;
      const x = Math.max(0, Number(t) || 0);
      if (v) {
        try {
          v.currentTime = x;
        } catch (e) {/* not loaded yet */}
      }
      this.setState({
        time: x
      });
    });
    _defineProperty(this, "togglePlay", () => {
      const v = this.vid.current;
      if (!v) return;
      if (!v.paused) {
        v.pause();
        return;
      }
      const {
        trim
      } = this.state;
      const d = this.durOf();
      const trimmed = d && (trim.start > 0.05 || trim.end < d - 0.05);
      if (trimmed && (v.currentTime < trim.start || v.currentTime >= trim.end - 0.1)) v.currentTime = trim.start;
      this._trimGuard = !!trimmed;
      v.play().catch(() => {});
    });
    _defineProperty(this, "playCut", () => {
      const v = this.vid.current;
      if (!v) return;
      if (!v.paused) {
        v.pause();
        return;
      }
      const segs = this.state.cut.segments.filter(sg => Number(sg.end) > Number(sg.start));
      if (!segs.length) return;
      this._trimGuard = false;
      v.currentTime = Number(this.state.cut.segments[0].start) || 0;
      this.setState({
        cutPlay: true,
        cutIdx: 0
      });
      v.play().catch(() => {});
    });
    /* ---------- shared save path ---------- */
    _defineProperty(this, "pickTool", k => {
      if (this.state.busy) return;
      this.setState({
        tab: "edit",
        tool: k,
        moreOpen: false
      });
      if (k === "cut" && !this.state.cut.segments.length) this.initCut();
      if (k === "moments" && this.state.moments.transcript === null) this.loadTranscriptForMoments();
    });
    _defineProperty(this, "closeTool", () => {
      if (this.state.busy) return;
      this.setState({
        tool: "",
        cutPlay: false
      });
    });
    _defineProperty(this, "afterSave", v => {
      const pick = Array.isArray(v) ? v[v.length - 1] : v;
      const id = pick && idStr(pick.id || pick.Id);
      this.set({
        tool: "",
        cut: CUT0,
        extPrompt: ""
      });
      this.props.onReload(id || null);
    });
    _defineProperty(this, "creditsOr", (e, fallback) => {
      if (e && (e.outOfCredits || e.status === 402)) this.set({
        credits: {
          buyUrl: e.data && e.data.buyUrl || BUY_URL
        }
      });else toast(e && e.message || fallback);
    });
    _defineProperty(this, "postEdit", (assetId, body) => safe(call(`${CONTENT}/assets/${enc(idStr(assetId))}/edits`, {
      method: "POST",
      body
    }), null));
    /** The trim rail (unchanged from video-edit.jsx): one ffmpeg cut, saved as a version, or a failed edit row. */
    _defineProperty(this, "trimToVersion", async (a, start, end, label, batchId) => {
      const r = await runAgent("content_video_trim_agent", `trim ${fmt(start)} to ${fmt(end)}`, {
        videoUrl: a.url,
        start: String(start),
        end: String(end),
        label: label || ""
      });
      const done = await waitAgent(runIdOf(r), 4000, 90);
      const ctx = done && done.ctx || {};
      const res = done && done.res || {};
      const url = ctx.outputUrl || res.outputUrl || ctx.urls && ctx.urls[0];
      if (!done.ok || !url) {
        await this.postEdit(a.id, {
          action: "trim",
          params: JSON.stringify({
            start,
            end
          }),
          status: "failed",
          error: done && done.timeout ? "timed out" : "no output",
          credits: 0,
          batchId
        });
        return null;
      }
      const saved = await this.postEdit(a.id, {
        action: "trim",
        params: JSON.stringify({
          start,
          end
        }),
        status: "done",
        url,
        width: ctx.width || res.width || null,
        height: ctx.height || res.height || null,
        durationSeconds: Math.round(Number(ctx.seconds || res.seconds || end - start)),
        posterUrl: ctx.posterUrl || res.posterUrl || null,
        label: label || `Trim ${fmt(start)}-${fmt(end)}`,
        credits: 0,
        batchId
      });
      return saved && saved.version;
    });
    /* ---------- Trim ---------- */
    _defineProperty(this, "setTrim", (start, end) => {
      const d = this.durOf();
      const s = Math.max(0, Math.min(Number(start) || 0, d || Infinity));
      const e = Math.max(0, Math.min(Number(end) || 0, d || Infinity));
      this.setState({
        trim: {
          start: s,
          end: e
        },
        tab: "edit",
        tool: this.state.tool === "cut" || this.state.tool === "redirect" ? this.state.tool : "trim"
      });
    });
    _defineProperty(this, "onTrimDrag", (start, end, which) => {
      this.setTrim(start, end);
      this.seek(which === "start" ? start : end);
    });
    _defineProperty(this, "saveTrim", async () => {
      const {
        start,
        end
      } = this.state.trim;
      const a = this.asset();
      if (!(end > start)) {
        toast("End must be after start.");
        return;
      }
      this.setState({
        busy: "Cutting the trim…"
      });
      try {
        const v = await this.trimToVersion(a, start, end, null, null);
        if (!v) {
          toast("The cut did not finish.");
          return;
        }
        toast(`Saved as v${v.version || v.Version}.`);
        this.afterSave(v);
      } catch (e) {
        toast(e && e.message || "Could not start the cut.");
      } finally {
        this.set({
          busy: ""
        });
      }
    });
    /* ---------- Extend ---------- */
    _defineProperty(this, "extend", async () => {
      const prompt = (this.state.extPrompt || "").trim();
      if (!prompt || this.state.busy) return;
      const steps = this.state.extSteps === 2 ? 2 : 1;
      const a = this.asset();
      const aspect = a.width && a.height && a.height > a.width ? "9:16" : "16:9";
      let src = {
        id: a.id,
        url: a.url
      };
      let last = null;
      try {
        for (let i = 0; i < steps; i++) {
          this.set({
            busy: steps > 1 ? `Extending, part ${i + 1} of ${steps}…` : "Extending…"
          });
          const r = await runAgent("content_video_extend_agent", prompt, {
            videoUrl: src.url,
            prompt,
            aspectRatio: aspect
          });
          const done = await waitAgent(runIdOf(r), 5000, 90);
          const ctx = done && done.ctx || {};
          const res = done && done.res || {};
          const url = ctx.outputUrl || res.outputUrl;
          if (!done.ok || !url) {
            toast(done && done.timeout ? "Still working: the extended version will appear in Assets." : "The extension did not finish.");
            break;
          }
          const saved = await this.postEdit(src.id, {
            action: "extend_video",
            prompt,
            status: "done",
            url,
            width: ctx.width || null,
            height: ctx.height || null,
            durationSeconds: Math.round(Number(ctx.seconds || 0)) || null,
            posterUrl: ctx.posterUrl || null,
            label: "Extended",
            credits: 40
          });
          const v = saved && saved.version;
          if (!v) {
            toast("Extended, but the version could not be saved.");
            break;
          }
          last = v;
          src = {
            id: idStr(v.id || v.Id),
            url: v.url || v.Url || url
          };
        }
        if (last) {
          toast(`Saved as v${last.version || last.Version}.`);
          this.afterSave(last);
        }
      } catch (e) {
        this.creditsOr(e, "Could not start the extension.");
        if (last) this.afterSave(last);
      } finally {
        this.set({
          busy: ""
        });
      }
    });
    /* ---------- Cut and caption ---------- */
    _defineProperty(this, "initCut", async () => {
      const a = this.asset();
      const d = Math.round(this.durOf() * 10) / 10;
      this.setState({
        cut: {
          ...CUT0,
          segments: [{
            start: 0,
            end: d,
            caption: "",
            overlay: "",
            label: "Clip 1"
          }],
          loading: true
        }
      });
      const [t, brand] = await Promise.all([safe(call(`${CONTENT}/assets/${enc(a.id)}/transcript`), null), safe(loadBrandKit(), {})]);
      if (!this._alive || this.asset().id !== a.id) return;
      const c = this.state.cut;
      const segs = c.segments.slice();
      if (segs.length === 1 && !c.dirty) {
        if (t && t.segments && t.segments.length) segs[0] = {
          ...segs[0],
          caption: t.segments.map(x => x.text).join(" ").slice(0, 600)
        };else if (t && t.text) segs[0] = {
          ...segs[0],
          caption: String(t.text).slice(0, 600)
        };
      }
      this.setState({
        cut: {
          ...c,
          segments: segs,
          transcript: t && (t.text || (t.segments || []).length) ? t : null,
          brand: brand || {},
          endTitle: c.endTitle || brand && brand.company || "",
          loading: false
        }
      });
    });
    _defineProperty(this, "cutSet", (patch, dirty) => this.setState({
      cut: {
        ...this.state.cut,
        ...patch,
        dirty: dirty === false ? this.state.cut.dirty : true
      }
    }));
    _defineProperty(this, "segSet", (i, patch) => this.cutSet({
      segments: this.state.cut.segments.map((sg, k) => k === i ? {
        ...sg,
        ...patch
      } : sg)
    }));
    _defineProperty(this, "splitAt", () => {
      const t = this.playhead();
      const segs = this.state.cut.segments;
      const i = segs.findIndex(sg => t > Number(sg.start) + 0.3 && t < Number(sg.end) - 0.3);
      if (i < 0) {
        toast("Move the playhead inside a clip to split it.");
        return;
      }
      const a = {
        ...segs[i],
        end: t,
        caption: this.captionFor(Number(segs[i].start), t) || segs[i].caption
      };
      const b = {
        ...segs[i],
        start: t,
        caption: this.captionFor(t, Number(segs[i].end)),
        overlay: "",
        label: `Clip ${segs.length + 1}`
      };
      this.cutSet({
        segments: [...segs.slice(0, i), a, b, ...segs.slice(i + 1)],
        sel: i + 1
      });
    });
    _defineProperty(this, "addClip", () => {
      const d = this.durOf();
      const t = Math.min(this.playhead(), Math.max(0, d - 1));
      const segs = this.state.cut.segments;
      const end = Math.round(Math.min(d || t + 5, t + 5) * 10) / 10;
      const at = Math.min(segs.length, this.state.cut.sel + 1);
      const seg = {
        start: t,
        end,
        caption: this.captionFor(t, end),
        overlay: "",
        label: `Clip ${segs.length + 1}`
      };
      this.cutSet({
        segments: [...segs.slice(0, at), seg, ...segs.slice(at)],
        sel: at
      });
    });
    _defineProperty(this, "moveSeg", (from, to) => {
      const segs = this.state.cut.segments.slice();
      if (to < 0 || to >= segs.length || from === to) return;
      const [x] = segs.splice(from, 1);
      segs.splice(to, 0, x);
      this.cutSet({
        segments: segs,
        sel: to
      });
    });
    _defineProperty(this, "removeSeg", i => {
      const segs = this.state.cut.segments;
      if (segs.length <= 1) return;
      this.cutSet({
        segments: segs.filter((_, k) => k !== i),
        sel: Math.max(0, Math.min(this.state.cut.sel, segs.length - 2))
      });
    });
    _defineProperty(this, "selectSeg", i => {
      const sg = this.state.cut.segments[i];
      this.cutSet({
        sel: i
      }, false);
      if (sg) this.seek(Number(sg.start) || 0);
    });
    _defineProperty(this, "saveCut", async () => {
      const a = this.asset();
      const c = this.state.cut;
      const brand = c.brand || {};
      const segs = c.segments.filter(sg => Number(sg.end) > Number(sg.start) + 0.2);
      if (!segs.length) {
        toast("Add at least one clip.");
        return;
      }
      const music = c.musicOn ? (c.music || "").trim() : "";
      const spec = {
        sourceUrl: a.url,
        segments: segs.map(sg => ({
          start: Number(sg.start),
          end: Number(sg.end),
          caption: c.captions ? sg.caption : "",
          overlay: sg.overlay || "",
          label: sg.label || ""
        })),
        aspectRatio: c.aspect || null,
        captions: c.captions,
        preset: {
          font: brand.captionFont || "DejaVu Sans",
          textColor: brand.captionTextColor || "#ffffff",
          backColor: brand.captionBackColor || "#000000",
          backOpacity: brand.captionBackOpacity != null ? Number(brand.captionBackOpacity) : 0.6,
          position: brand.captionPosition || "bottom",
          size: brand.captionSize != null ? Number(brand.captionSize) : 0.044,
          bold: true
        },
        musicUrl: music || null,
        musicVolume: 0.3,
        crossfade: true,
        endCard: c.endCard && c.endTitle.trim() ? {
          title: c.endTitle.trim(),
          subtitle: c.endSub.trim() || null,
          brandColor: brand.accent || "#1a1a1a",
          textColor: "#ffffff",
          logoUrl: brand.logoUrl && !/\.svg(\?|$)/i.test(brand.logoUrl) ? brand.logoUrl : null,
          seconds: 2.5
        } : null,
        saveSegmentsSeparately: c.separate
      };
      const v = this.vid.current;
      if (v && !v.paused) v.pause();
      this.setState({
        busy: "Rendering the cut…"
      });
      try {
        const r = await runAgent("content_video_edit_agent", "Render the edit", {
          spec: JSON.stringify(spec)
        });
        const done = await waitAgent(runIdOf(r), 5000, 120);
        const ctx = done && done.ctx || {};
        const res = done && done.res || {};
        const results = ctx.results || res.results || [];
        if (!done.ok || !results.length) {
          toast(done && done.timeout ? "Still rendering: the version will appear in Assets." : "The render did not finish.");
          return;
        }
        const batchId = uuid();
        let last = null;
        for (let i = 0; i < results.length; i++) {
          const x = results[i];
          const saved = await this.postEdit(a.id, {
            action: "video_edit",
            params: JSON.stringify({
              segments: spec.segments.length,
              captions: c.captions,
              aspect: c.aspect || null,
              endCard: !!spec.endCard
            }),
            status: "done",
            url: x.url,
            width: x.width || null,
            height: x.height || null,
            durationSeconds: Math.round(Number(x.seconds || 0)) || null,
            posterUrl: x.posterUrl || null,
            label: results.length > 1 ? x.label || `Clip ${i + 1}` : "Edit",
            credits: 0,
            batchId
          });
          if (saved && saved.version) last = saved.version;
        }
        toast(results.length > 1 ? `${results.length} clips saved as versions.` : `Saved as v${last ? last.version || last.Version : "?"}.`);
        this.afterSave(last);
      } catch (e) {
        toast(e && e.message || "Could not start the render.");
      } finally {
        this.set({
          busy: ""
        });
      }
    });
    /* ---------- Find moments ---------- */
    _defineProperty(this, "loadTranscriptForMoments", async () => {
      const a = this.asset();
      const t = await safe(call(`${CONTENT}/assets/${enc(a.id)}/transcript`), null);
      if (this._alive && this.asset().id === a.id) this.setState({
        moments: {
          ...this.state.moments,
          transcript: t && (t.text || (t.segments || []).length) ? t : false
        }
      });
    });
    _defineProperty(this, "momSet", patch => this.setState({
      moments: {
        ...this.state.moments,
        ...patch
      }
    }));
    _defineProperty(this, "findMoments", async () => {
      const a = this.asset();
      const {
        transcript,
        target
      } = this.state.moments;
      this.setState({
        busy: "Finding moments…",
        moments: {
          ...this.state.moments,
          clips: undefined
        }
      });
      try {
        const p = {
          videoUrl: a.url,
          target
        };
        if (transcript && transcript.text) p.transcript = String(transcript.text).slice(0, 6000);
        if (transcript && transcript.segments && transcript.segments.length) p.segments = JSON.stringify(transcript.segments.slice(0, 120));
        const r = await runAgent("content_clip_agent", "Propose clips", p);
        const done = await waitAgent(runIdOf(r), 4000, 60);
        let clips = done && done.res && Array.isArray(done.res.clips) ? done.res.clips : null;
        if (!clips) {
          try {
            const raw = String(done && done.run && (done.run.runResult || done.run.RunResult) || "");
            const j = JSON.parse(raw.slice(raw.indexOf("{"), raw.lastIndexOf("}") + 1));
            clips = j.clips;
          } catch (e) {
            clips = null;
          }
        }
        clips = (clips || []).filter(c => c && Number(c.end) > Number(c.start)).slice(0, 8).map(c => ({
          start: Math.round(Number(c.start) * 10) / 10,
          end: Math.round(Number(c.end) * 10) / 10,
          label: c.label || "Clip",
          why: c.why || ""
        }));
        if (this._alive) this.momSet({
          clips,
          picked: new Set(clips.map((_, i) => i))
        });
        if (!clips.length) toast(done && done.timeout ? "Still looking: try again in a minute." : "No moments were suggested for this video.");
      } catch (e) {
        toast(e && e.message || "Could not run the clip agent.");
      } finally {
        this.set({
          busy: ""
        });
      }
    });
    _defineProperty(this, "makeMoments", async () => {
      const {
        clips,
        picked
      } = this.state.moments;
      const a = this.asset();
      const chosen = (clips || []).filter((_, i) => picked.has(i));
      if (!chosen.length) return;
      const batchId = uuid();
      let made = 0;
      let last = null;
      try {
        for (let i = 0; i < chosen.length; i++) {
          const c = chosen[i];
          this.set({
            busy: `Cutting ${i + 1} of ${chosen.length}…`
          });
          const v = await this.trimToVersion(a, c.start, c.end, c.label, batchId);
          if (v) {
            made++;
            last = v;
          }
        }
        toast(`${made} of ${chosen.length} clip${chosen.length === 1 ? "" : "s"} saved as versions.`);
        if (last) this.afterSave(last);
      } catch (e) {
        toast(e && e.message || "Could not make the clips.");
      } finally {
        this.set({
          busy: ""
        });
      }
    });
    /* ---------- Sizes ---------- */
    _defineProperty(this, "sizeSet", patch => this.setState({
      sizes: {
        ...this.state.sizes,
        ...patch
      }
    }));
    _defineProperty(this, "makeSizes", async () => {
      const a = this.asset();
      const {
        safe: safeRect,
        fill,
        fillColor,
        picked
      } = this.state.sizes;
      const targets = VIDEO_TARGETS.filter(([k]) => picked.has(k)).map(([, label, width, height]) => ({
        label,
        width,
        height
      }));
      if (!targets.length) return;
      this.setState({
        busy: `Making ${targets.length} size${targets.length === 1 ? "" : "s"}…`,
        sizes: {
          ...this.state.sizes,
          results: null,
          drawing: false
        }
      });
      const batchId = uuid();
      try {
        const p = {
          videoUrl: a.url,
          targets: JSON.stringify(targets),
          fill,
          fillColor
        };
        if (safeRect) p.safe = JSON.stringify(safeRect);
        const r = await runAgent("content_video_resize_agent", `Resize to ${targets.length} sizes`, p);
        const done = await waitAgent(runIdOf(r), 5000, 120);
        const ctx = done && done.ctx || {};
        const res = done && done.res || {};
        const results = ctx.results || res.results || [];
        if (!done.ok || !results.length) {
          toast(done && done.timeout ? "Still working: the sizes will appear in Assets." : "The resize did not finish.");
          return;
        }
        const saved = [];
        for (const x of results) {
          const s = await this.postEdit(a.id, {
            action: "video_resize",
            params: JSON.stringify({
              ...p,
              target: x.label
            }),
            status: "done",
            url: x.url,
            width: x.width,
            height: x.height,
            durationSeconds: Math.round(Number(x.seconds || 0)) || null,
            posterUrl: x.posterUrl || null,
            label: `${x.label} ${x.width}x${x.height}`,
            credits: 0,
            batchId
          });
          saved.push({
            ...x,
            version: s && s.version
          });
        }
        this.set({
          sizes: {
            ...this.state.sizes,
            results: saved
          }
        });
        toast(`${saved.length} size${saved.length === 1 ? "" : "s"} saved as versions.`);
        const vs = saved.map(x => x.version).filter(Boolean);
        if (vs.length) this.props.onReload(idStr(vs[vs.length - 1].id || vs[vs.length - 1].Id));
      } catch (e) {
        toast(e && e.message || "Could not start the resize.");
      } finally {
        this.set({
          busy: ""
        });
      }
    });
    /* ---------- Cover image ---------- */
    _defineProperty(this, "coverSet", patch => this.setState({
      cover: {
        ...this.state.cover,
        ...patch
      }
    }));
    _defineProperty(this, "setPoster", async posterUrl => {
      const a = this.asset();
      const root = this.props.family[0];
      const ids = [a.id];
      if (this.state.cover.alsoRoot && root && root.id !== a.id) ids.push(root.id);
      for (const id of ids) await call(`${CONTENT}/assets/${enc(idStr(id))}/poster`, {
        method: "POST",
        body: {
          posterUrl
        }
      });
      toast("Cover saved. It shows in Assets and wherever this video is used.");
      this.set({
        tool: ""
      });
      this.props.onReload(a.id);
    });
    _defineProperty(this, "saveCoverFrame", async () => {
      const a = this.asset();
      const t = this.playhead();
      const v = this.vid.current;
      if (v && !v.paused) v.pause();
      this.setState({
        busy: "Saving the cover…"
      });
      try {
        let blob;
        try {
          blob = await captureFrame(a.url, t);
        } catch (e) {
          this.set({
            cover: {
              ...this.state.cover,
              tainted: true,
              mode: "image"
            }
          });
          toast("This video's frames cannot be read in the browser. Pick an image as the cover instead.");
          return;
        }
        const up = await call(`${CONTENT}/assets/upload-url`, {
          method: "POST",
          body: {
            fileName: "cover.jpg",
            contentType: "image/jpeg",
            bytes: blob.size
          }
        });
        if (!up || !up.uploadUrl) throw new Error("Could not prepare the upload.");
        const put = await fetch(up.uploadUrl, {
          method: "PUT",
          headers: {
            "content-type": up.contentType || "image/jpeg"
          },
          body: blob
        });
        if (!put.ok) throw new Error(`Upload failed (${put.status})`);
        await this.setPoster(up.url);
      } catch (e) {
        toast(e && e.message || "Could not save the cover.");
      } finally {
        this.set({
          busy: ""
        });
      }
    });
    _defineProperty(this, "saveCoverImage", async () => {
      const {
        imageId,
        assets
      } = this.state.cover;
      const img = (assets || []).find(x => idStr(x.id) === idStr(imageId));
      const url = img && (img.latestUrl || img.url);
      if (!url) {
        toast("Pick an image first.");
        return;
      }
      this.setState({
        busy: "Saving the cover…"
      });
      try {
        await this.setPoster(url);
      } catch (e) {
        toast(e && e.message || "Could not save the cover.");
      } finally {
        this.set({
          busy: ""
        });
      }
    });
  }
  componentDidMount() {
    this.loadFrames();
    document.addEventListener("mousedown", this.onDocDown);
  }
  componentWillUnmount() {
    this._alive = false;
    cancelAnimationFrame(this._raf);
    clearTimeout(this._recover);
    document.removeEventListener("mousedown", this.onDocDown);
  }
  componentDidUpdate(prev) {
    if (prev.asset.id !== this.props.asset.id) {
      const d = Number(this.props.asset.durationSeconds) || 0;
      cancelAnimationFrame(this._raf);
      this._recovered = false;
      this._trimGuard = false;
      const tool = this.state.tool;
      this.setState({
        time: 0,
        dur: d,
        playing: false,
        natural: null,
        trim: {
          start: 0,
          end: d
        },
        frames: null,
        cutPlay: false,
        cut: CUT0,
        moments: {
          ...this.state.moments,
          clips: undefined,
          picked: new Set(),
          transcript: null
        },
        cover: {
          ...this.state.cover,
          tainted: false,
          imageId: ""
        }
      }, () => {
        if (tool === "cut") this.initCut();
        if (tool === "moments") this.loadTranscriptForMoments();
      });
      this.loadFrames();
    }
  }
  asset() {
    return this.props.asset;
  }
  durOf() {
    return this.state.dur || Number(this.props.asset.durationSeconds) || 0;
  }
  nextVersion() {
    return Math.max(...this.props.family.map(x => x.version || 1)) + 1;
  }
  playhead() {
    const v = this.vid.current;
    return Math.round(((v ? v.currentTime : this.state.time) || 0) * 10) / 10;
  }
  captionFor(start, end) {
    const t = this.state.cut.transcript;
    if (t && t.segments && t.segments.length) return t.segments.filter(x => Number(x.end) > start && Number(x.start) < end).map(x => x.text).join(" ").slice(0, 600);
    return "";
  }
  /* =================================================================== render */
  render() {
    const {
      family,
      hist,
      recipe,
      history
    } = this.props;
    const a = this.asset();
    const root = family[0];
    const s = this.state;
    const d = this.durOf();
    const busy = s.busy;
    const latest = family[family.length - 1];
    const row = hist.find(h => h.versionId === a.id) || {};
    const dims = a.width && a.height ? `${a.width} × ${a.height}` : s.natural ? `${s.natural.w} × ${s.natural.h}` : "";
    const sub = [`v${a.version} of ${family.length}`, d ? fmt(d) : "", dims, row.label || ""].filter(Boolean).join(" · ");
    const trimDirty = d && (s.trim.start > 0.05 || s.trim.end < d - 0.05);
    const savedLine = busy ? /*#__PURE__*/React.createElement("span", {
      className: "cx-saved dim"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons spin"
    }, "autorenew"), "Working") : s.tool === "cut" && s.cut.dirty ? /*#__PURE__*/React.createElement("span", {
      className: "cx-saved dim"
    }, "Unsaved cut") : trimDirty ? /*#__PURE__*/React.createElement("span", {
      className: "cx-saved dim"
    }, "Unsaved trim") : /*#__PURE__*/React.createElement("span", {
      className: "cx-saved"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "check"), "Saved");
    const seqHref = `/Content/sequences/new?pick=${enc(a.id)}&from=${enc(`${root.title || "Clip"} · ${vlabel(a)}`)}`;
    return /*#__PURE__*/React.createElement("div", {
      className: "cx"
    }, /*#__PURE__*/React.createElement("div", {
      className: "cx-top"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "cx-iconbtn",
      "aria-label": this._back ? "Back to the piece" : "Back to Assets",
      title: this._back ? "Back to the piece" : "Back to Assets",
      onClick: () => history.push(this._back || "/Content")
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "chevron_left")), /*#__PURE__*/React.createElement("div", {
      className: "cx-title"
    }, /*#__PURE__*/React.createElement("b", {
      title: root.title || ""
    }, root.title || "Clip"), /*#__PURE__*/React.createElement("span", null, sub)), a.id === latest.id ? /*#__PURE__*/React.createElement("span", {
      className: "plk-chip"
    }, "Latest") : /*#__PURE__*/React.createElement("span", {
      className: "plk-chip warn",
      title: "A newer version exists. Edits from here save as a new version too."
    }, "Older version"), /*#__PURE__*/React.createElement("span", {
      className: "sp"
    }), savedLine, /*#__PURE__*/React.createElement("span", {
      className: "cx-more",
      ref: this.more
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "cx-iconbtn",
      "aria-label": "More actions",
      "aria-expanded": s.moreOpen,
      title: "More actions",
      onClick: () => this.setState({
        moreOpen: !s.moreOpen
      })
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "more_horiz")), s.moreOpen ? /*#__PURE__*/React.createElement("span", {
      className: "cx-menu",
      role: "menu"
    }, /*#__PURE__*/React.createElement("a", {
      role: "menuitem",
      href: a.url,
      target: "_blank",
      rel: "noreferrer",
      onClick: () => this.setState({
        moreOpen: false
      })
    }, "Open the file in a new tab"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      role: "menuitem",
      onClick: () => {
        this.setState({
          moreOpen: false
        });
        try {
          navigator.clipboard.writeText(a.url).then(() => toast("Link copied."), () => toast(a.url));
        } catch (e) {
          toast(a.url);
        }
      }
    }, "Copy the file link"), root.id !== a.id ? /*#__PURE__*/React.createElement("a", {
      role: "menuitem",
      href: root.url,
      target: "_blank",
      rel: "noreferrer",
      download: true,
      onClick: () => this.setState({
        moreOpen: false
      })
    }, "Download the original") : null, /*#__PURE__*/React.createElement("button", {
      type: "button",
      role: "menuitem",
      onClick: () => history.push("/Content/clips/new")
    }, "Make a new clip"), hasBothApps() ? /*#__PURE__*/React.createElement("a", {
      role: "menuitem",
      href: `${studioUrl()}/?asset=${enc(a.id)}`,
      target: "_blank",
      rel: "noreferrer",
      onClick: () => this.setState({
        moreOpen: false
      })
    }, "Open in Studio") : null) : null), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "cx-btn",
      title: "Start a narrated sequence with this version as the first piece",
      onClick: () => history.push(seqHref)
    }, "Use in a sequence"), /*#__PURE__*/React.createElement("a", {
      className: "cx-primary",
      href: a.url,
      target: "_blank",
      rel: "noreferrer",
      download: true,
      title: `Download ${vlabel(a)}`
    }, "Download")), /*#__PURE__*/React.createElement("div", {
      className: "cx-body"
    }, /*#__PURE__*/React.createElement("div", {
      className: "cx-left"
    }, this.renderStage(a, d), this.renderFilm(a)), /*#__PURE__*/React.createElement("div", {
      className: "cx-side"
    }, /*#__PURE__*/React.createElement("div", {
      className: "cx-tabs",
      role: "tablist"
    }, [["edit", "Edit"], ["sizes", "Sizes"], ["details", "Details"]].map(([k, l]) => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: k,
      role: "tab",
      "aria-selected": s.tab === k,
      className: s.tab === k ? "on" : "",
      onClick: () => this.setState({
        tab: k,
        sizes: {
          ...s.sizes,
          drawing: k === "sizes" ? s.sizes.drawing : false
        }
      })
    }, l))), /*#__PURE__*/React.createElement("div", {
      className: "cx-pane"
    }, s.tab === "edit" ? this.renderTools(a, recipe, root) : s.tab === "sizes" ? this.renderSizes(a) : this.renderDetails(a, root, recipe)))));
  }
  renderStage(a, d) {
    const s = this.state;
    const busy = s.busy;
    const w = a.width || s.natural && s.natural.w || 16;
    const h = a.height || s.natural && s.natural.h || 9;
    const ratio = w / h;
    const cutOpen = s.tool === "cut";
    const segs = s.cut.segments;
    const segIdx = cutOpen ? s.cutPlay ? s.cutIdx : segs.findIndex(sg => s.time >= Number(sg.start) && s.time < Number(sg.end)) : -1;
    const curSeg = segIdx >= 0 ? segs[segIdx] : null;
    const sizesOn = s.tab === "sizes";
    const momentRanges = s.tool === "moments" && s.moments.clips ? s.moments.clips.filter((_, i) => s.moments.picked.has(i)) : [];
    const cutTime = s.cutPlay ? segs.slice(0, s.cutIdx).reduce((n, sg) => n + Math.max(0, Number(sg.end) - Number(sg.start)), 0) + Math.max(0, s.time - Number((segs[s.cutIdx] || {}).start || 0)) : 0;
    return /*#__PURE__*/React.createElement("div", {
      className: "cx-stage"
    }, /*#__PURE__*/React.createElement("div", {
      className: "cx-col"
    }, /*#__PURE__*/React.createElement("div", {
      className: "cx-frame",
      style: {
        aspectRatio: `${w} / ${h}`,
        width: `min(100%, calc(${ratio.toFixed(4)} * min(52vh, 470px)))`
      }
    }, /*#__PURE__*/React.createElement("video", {
      key: a.id,
      ref: this.vid,
      src: a.url,
      poster: a.posterUrl || undefined,
      controls: !(sizesOn && s.sizes.drawing),
      playsInline: true,
      preload: "metadata",
      onLoadedMetadata: this.onMeta,
      onPlay: this.onPlay,
      onPause: this.onPause,
      onEnded: this.onPause,
      onSeeked: e => {
        if (!s.playing) this.setState({
          time: e.target.currentTime
        });
      }
    }), sizesOn && (s.sizes.drawing || s.sizes.safe) ? /*#__PURE__*/React.createElement(SafeZoneLayer, {
      rect: s.sizes.safe,
      drawing: s.sizes.drawing && !busy,
      onRect: r => this.sizeSet({
        safe: r,
        drawing: false
      })
    }) : null, sizesOn && s.sizes.drawing ? /*#__PURE__*/React.createElement("span", {
      className: "cx-hint"
    }, "Drag a box around what must stay in every size.") : null, cutOpen && curSeg ? /*#__PURE__*/React.createElement("span", {
      className: "cx-hint"
    }, s.cutPlay ? "Preview of the cut · " : "", "clip ", segIdx + 1, " of ", segs.length) : null, cutOpen && curSeg && curSeg.overlay ? /*#__PURE__*/React.createElement("span", {
      className: "cx-ovl"
    }, curSeg.overlay) : null, busy ? /*#__PURE__*/React.createElement("span", {
      className: "cx-busy",
      role: "status"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons spin"
    }, "autorenew"), busy) : null), s.credits ? /*#__PURE__*/React.createElement("div", {
      className: "cx-notice",
      role: "status"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "info"), /*#__PURE__*/React.createElement("span", null, "Not enough credits for this."), /*#__PURE__*/React.createElement("span", {
      className: "sp"
    }), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "cx-go sm",
      onClick: () => window.open(s.credits.buyUrl, "_blank", "noopener")
    }, "Buy credits"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "cx-btn sm",
      onClick: () => this.setState({
        credits: null
      })
    }, "Dismiss")) : null, cutOpen ? /*#__PURE__*/React.createElement(CutTimeline, {
      segments: segs,
      sel: s.cut.sel,
      dur: d,
      frames: s.frames,
      poster: a.posterUrl,
      count: FRAMES,
      captions: s.cut.captions,
      playing: s.playing && s.cutPlay,
      cutTime: cutTime,
      onSelect: this.selectSeg,
      onMove: this.moveSeg,
      onPlayCut: this.playCut
    }) : /*#__PURE__*/React.createElement(TrimTimeline, {
      dur: d,
      time: s.time,
      start: s.trim.start,
      end: s.trim.end || d,
      playing: s.playing,
      onPlay: this.togglePlay,
      onSeek: this.seek,
      onTrim: this.onTrimDrag,
      frames: s.frames,
      poster: a.posterUrl,
      count: FRAMES,
      disabled: !!busy || s.tool === "redirect",
      ranges: momentRanges,
      hint: s.tool === "moments" && momentRanges.length ? "Marked: the moments you picked" : null
    }), !cutOpen && s.tool !== "redirect" ? /*#__PURE__*/React.createElement("div", {
      className: "cx-prompt"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons spark"
    }, "auto_awesome"), /*#__PURE__*/React.createElement("label", {
      className: "in"
    }, /*#__PURE__*/React.createElement("span", {
      className: "sr"
    }, "What happens next"), /*#__PURE__*/React.createElement("input", {
      value: s.extPrompt,
      disabled: !!busy,
      placeholder: "What happens next, e.g. the camera slowly pulls back",
      onChange: e => this.setState({
        extPrompt: e.target.value
      }),
      onKeyDown: e => {
        if (e.key === "Enter") this.extend();
      }
    })), this.extSeg(), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "cx-go",
      disabled: !!busy || !s.extPrompt.trim(),
      onClick: this.extend
    }, busy && /^Extending/.test(busy) ? "Extending…" : "Extend")) : null));
  }
  extSeg() {
    const s = this.state;
    return /*#__PURE__*/React.createElement("span", {
      className: "cx-seg",
      role: "group",
      "aria-label": "How much to add"
    }, [[1, `+ ${EXTEND_SECONDS} s`, "One generated extension"], [2, `+ ${EXTEND_SECONDS * 2} s`, "Two extensions in a row, each saved as a version"]].map(([k, l, t]) => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: k,
      title: t,
      "aria-pressed": s.extSteps === k,
      className: s.extSteps === k ? "on" : "",
      disabled: !!s.busy,
      onClick: () => this.setState({
        extSteps: k
      })
    }, l)));
  }

  /** Versions as a filmstrip under the stage: click one to keep editing from it. */
  renderFilm(a) {
    const {
      family
    } = this.props;
    const s = this.state;
    const pending = s.busy ? "Working…" : s.tool === "cut" && s.cut.dirty ? `v${this.nextVersion()}` : "";
    return /*#__PURE__*/React.createElement("div", {
      className: "cx-film"
    }, /*#__PURE__*/React.createElement("span", {
      className: "cx-eyebrow"
    }, "Versions"), /*#__PURE__*/React.createElement("div", {
      className: "cx-strip-v"
    }, family.map((x, i) => {
      const row = this.props.hist.find(h => h.versionId === x.id) || {};
      return /*#__PURE__*/React.createElement(React.Fragment, {
        key: x.id
      }, i > 0 ? /*#__PURE__*/React.createElement("span", {
        className: "material-icons arrow",
        "aria-hidden": "true"
      }, "chevron_right") : null, /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "cx-ver" + (x.id === a.id ? " sel" : ""),
        disabled: !!s.busy,
        "aria-pressed": x.id === a.id,
        title: `${vlabel(x)} · ${row.label || "Version"}${x.title ? ` · ${x.title}` : ""}`,
        onClick: () => this.props.onSelect(x.id)
      }, /*#__PURE__*/React.createElement("span", {
        className: "th"
      }, x.posterUrl ? /*#__PURE__*/React.createElement("img", {
        src: x.posterUrl,
        alt: "",
        loading: "lazy"
      }) : x.url ? /*#__PURE__*/React.createElement("video", {
        src: `${x.url}#t=0.5`,
        muted: true,
        playsInline: true,
        preload: "metadata",
        tabIndex: -1,
        "aria-hidden": "true"
      }) : /*#__PURE__*/React.createElement("span", {
        className: "material-icons"
      }, "movie"), x.durationSeconds ? /*#__PURE__*/React.createElement("i", null, fmt(x.durationSeconds)) : null), /*#__PURE__*/React.createElement("span", {
        className: "tx"
      }, /*#__PURE__*/React.createElement("b", null, "v", x.version), " ", x.version <= 1 ? "Original" : row.label || "Version")));
    }), pending ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      className: "material-icons arrow",
      "aria-hidden": "true"
    }, "chevron_right"), /*#__PURE__*/React.createElement("span", {
      className: "cx-ver pending"
    }, /*#__PURE__*/React.createElement("span", {
      className: "th"
    }, /*#__PURE__*/React.createElement("em", null, pending)), /*#__PURE__*/React.createElement("span", {
      className: "tx"
    }, s.busy ? "new version" : "this cut"))) : null), /*#__PURE__*/React.createElement("span", {
      className: "cx-filmhint"
    }, "Click any version to keep editing from it. Nothing is ever overwritten."));
  }
  renderTools(a, recipe, root) {
    const s = this.state;
    const busy = !!s.busy;
    const tool = s.tool;
    if (tool && TOOL_INFO[tool]) {
      const [name, desc] = TOOL_INFO[tool];
      return /*#__PURE__*/React.createElement("div", {
        className: "cx-tool"
      }, /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "cx-back",
        disabled: busy,
        onClick: this.closeTool
      }, /*#__PURE__*/React.createElement("span", {
        className: "material-icons"
      }, "chevron_left"), "All tools"), /*#__PURE__*/React.createElement("h3", null, name), /*#__PURE__*/React.createElement("p", null, desc), tool === "trim" ? this.renderTrim(a) : tool === "cut" ? this.renderCut(a) : tool === "moments" ? this.renderMoments(a) : tool === "extend" ? this.renderExtend(a) : tool === "cover" ? this.renderCover(a, root) : tool === "redirect" ? this.renderRedirect(recipe, root) : null);
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      className: "cx-eyebrow"
    }, "Edit ", vlabel(a)), /*#__PURE__*/React.createElement("div", {
      className: "cx-tiles"
    }, TILES.filter(([k]) => k !== "redirect" || recipe).map(([k, icon, l]) => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: k,
      className: "cx-tile",
      disabled: busy,
      title: TOOL_INFO[k][1],
      onClick: () => this.pickTool(k)
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, icon), /*#__PURE__*/React.createElement("b", null, l)))), recipe ? /*#__PURE__*/React.createElement("div", {
      className: "cx-card"
    }, /*#__PURE__*/React.createElement("b", null, "Directed from an image"), /*#__PURE__*/React.createElement("span", null, "Re-direct reopens the same shots and length so you can change them. The result saves as a new version.")) : null, /*#__PURE__*/React.createElement("div", {
      className: "cx-card"
    }, /*#__PURE__*/React.createElement("b", null, "Edits never overwrite"), /*#__PURE__*/React.createElement("span", null, "Tools work on the selected version and save a new one. Pick another version below to edit from it.")));
  }
  foot(hint, label, onClick, disabled, kind) {
    return /*#__PURE__*/React.createElement("div", {
      className: "cx-foot"
    }, /*#__PURE__*/React.createElement("span", null, hint), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: kind === "base" ? "cx-btn" : "cx-go",
      disabled: disabled,
      onClick: onClick
    }, label));
  }
  timeField(label, value, onChange, onPlayhead, onBlur) {
    return /*#__PURE__*/React.createElement("label", {
      className: "cx-field cx-time-f"
    }, label, /*#__PURE__*/React.createElement("span", {
      className: "row"
    }, /*#__PURE__*/React.createElement("input", {
      type: "number",
      min: "0",
      step: "0.1",
      value: value,
      onChange: e => onChange(num(e.target.value, 0)),
      onBlur: onBlur
    }), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "cx-btn sm",
      title: "Use the playhead",
      onClick: onPlayhead
    }, "Playhead")));
  }
  renderTrim(a) {
    const s = this.state;
    const busy = !!s.busy;
    const d = this.durOf();
    const {
      start,
      end
    } = s.trim;
    const whole = !d || start <= 0.05 && end >= d - 0.05;
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "cx-pair"
    }, this.timeField("Start", start, v => this.setTrim(v, end), () => this.setTrim(this.playhead(), end), () => this.seek(start)), this.timeField("End", end, v => this.setTrim(start, v), () => this.setTrim(start, this.playhead()), () => this.seek(end))), /*#__PURE__*/React.createElement("div", {
      className: "cx-chips"
    }, [6, 15, 30].filter(n => !d || n < d).map(n => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: n,
      className: "cx-chip",
      disabled: busy,
      onClick: () => this.setTrim(start, Math.min(d || start + n, Math.round((start + n) * 10) / 10))
    }, n, " s from start")), d ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "cx-chip",
      disabled: busy,
      onClick: () => this.setTrim(0, d)
    }, "Whole video") : null), /*#__PURE__*/React.createElement("p", {
      className: "cx-note"
    }, "Keeps ", fmt(Math.max(0, end - start)), ". Sound is kept and the original stays untouched."), this.foot(whole ? "Drag the ends of the timeline to choose a part" : `Saves as v${this.nextVersion()} · ${fmt(end - start)}`, s.busy && /trim/.test(s.busy) ? "Cutting…" : "Save the trim", this.saveTrim, busy || !(end > start) || whole));
  }
  renderExtend(a) {
    const s = this.state;
    const busy = !!s.busy;
    const d = this.durOf();
    const add = EXTEND_SECONDS * (s.extSteps === 2 ? 2 : 1);
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("label", {
      className: "cx-field"
    }, "What happens next", /*#__PURE__*/React.createElement("textarea", {
      rows: 3,
      value: s.extPrompt,
      disabled: busy,
      placeholder: "e.g. the camera slowly pulls back",
      onChange: e => this.setState({
        extPrompt: e.target.value
      })
    })), /*#__PURE__*/React.createElement("div", {
      className: "cx-field"
    }, "How much to add ", this.extSeg()), /*#__PURE__*/React.createElement("p", {
      className: "cx-note"
    }, "Each run adds ", EXTEND_SECONDS, " seconds from the last frame of the whole video. ", s.extSteps === 2 ? "Two runs go back to back; each one is saved as a version." : "", " Takes a few minutes. The bar under the timeline does the same thing."), this.foot(d ? `${fmt(d)} becomes ${fmt(d + add)} · saved as v${this.nextVersion()}` : `Saved as v${this.nextVersion()}`, busy && /^Extending/.test(s.busy) ? "Extending…" : "Extend", this.extend, busy || !s.extPrompt.trim()));
  }
  renderCut(a) {
    const s = this.state;
    const busy = !!s.busy;
    const c = s.cut;
    const segs = c.segments;
    const sel = Math.min(c.sel, segs.length - 1);
    const seg = segs[sel];
    const total = segs.reduce((n, sg) => n + Math.max(0, Number(sg.end) - Number(sg.start)), 0);
    const brand = c.brand || {};
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "cx-group"
    }, /*#__PURE__*/React.createElement("span", {
      className: "cx-gh"
    }, "Clips ", /*#__PURE__*/React.createElement("i", null, "\xB7 ", segs.length, " kept \xB7 ", fmt(total))), /*#__PURE__*/React.createElement("div", {
      className: "cx-clips"
    }, segs.map((sg, i) => /*#__PURE__*/React.createElement("div", {
      key: i,
      className: "cx-cliprow" + (i === sel ? " sel" : "")
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "pick",
      "aria-pressed": i === sel,
      onClick: () => this.selectSeg(i)
    }, /*#__PURE__*/React.createElement("b", null, i + 1), /*#__PURE__*/React.createElement("span", {
      className: "t"
    }, fmt(sg.start), " to ", fmt(sg.end)), /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, Math.max(0, Math.round(Number(sg.end) - Number(sg.start))), " s")), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ic",
      title: "Move up",
      "aria-label": `Move clip ${i + 1} up`,
      disabled: busy || i === 0,
      onClick: () => this.moveSeg(i, i - 1)
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "arrow_upward")), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ic",
      title: "Move down",
      "aria-label": `Move clip ${i + 1} down`,
      disabled: busy || i === segs.length - 1,
      onClick: () => this.moveSeg(i, i + 1)
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "arrow_downward")), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ic",
      title: "Remove",
      "aria-label": `Remove clip ${i + 1}`,
      disabled: busy || segs.length <= 1,
      onClick: () => this.removeSeg(i)
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "close"))))), /*#__PURE__*/React.createElement("div", {
      className: "cx-chips"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "cx-chip dashed",
      disabled: busy,
      onClick: this.addClip
    }, "+ Add a clip at the playhead"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "cx-chip",
      disabled: busy,
      onClick: this.splitAt
    }, "Split at the playhead"))), seg ? /*#__PURE__*/React.createElement("div", {
      className: "cx-group"
    }, /*#__PURE__*/React.createElement("span", {
      className: "cx-gh"
    }, "Clip ", sel + 1), /*#__PURE__*/React.createElement("div", {
      className: "cx-pair"
    }, this.timeField("In", seg.start, v => this.segSet(sel, {
      start: v
    }), () => this.segSet(sel, {
      start: this.playhead()
    })), this.timeField("Out", seg.end, v => this.segSet(sel, {
      end: v
    }), () => this.segSet(sel, {
      end: this.playhead()
    }))), c.separate ? /*#__PURE__*/React.createElement("label", {
      className: "cx-field"
    }, "Name", /*#__PURE__*/React.createElement("input", {
      value: seg.label || "",
      placeholder: `Clip ${sel + 1}`,
      onChange: e => this.segSet(sel, {
        label: e.target.value
      })
    })) : null) : null, /*#__PURE__*/React.createElement("div", {
      className: "cx-group"
    }, /*#__PURE__*/React.createElement("span", {
      className: "cx-gh"
    }, "Words on screen"), /*#__PURE__*/React.createElement("label", {
      className: "cx-check"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: c.captions,
      disabled: busy,
      onChange: e => this.cutSet({
        captions: e.target.checked
      })
    }), "Captions from what is said"), c.captions ? /*#__PURE__*/React.createElement("span", {
      className: "cx-note"
    }, c.loading ? "Reading the transcript…" : c.transcript ? `Filled from the transcript. Brand style: ${(brand.captionFont || "DejaVu Sans").replace("DejaVu ", "")}, ${brand.captionPosition || "bottom"} (Settings › Brand kit › Captions).` : "No transcript for this video: type the words for each clip.") : null, seg ? /*#__PURE__*/React.createElement("label", {
      className: "cx-field"
    }, "Text on clip ", sel + 1, /*#__PURE__*/React.createElement("input", {
      value: seg.overlay || "",
      disabled: busy,
      placeholder: "A few words shown over this clip (optional)",
      onChange: e => this.segSet(sel, {
        overlay: e.target.value
      })
    })) : null, seg && c.captions ? /*#__PURE__*/React.createElement("label", {
      className: "cx-field"
    }, "Caption for clip ", sel + 1, /*#__PURE__*/React.createElement("textarea", {
      rows: 2,
      value: seg.caption || "",
      disabled: busy,
      placeholder: c.transcript ? "Words from the transcript for this clip" : "Words spoken in this clip",
      onChange: e => this.segSet(sel, {
        caption: e.target.value
      })
    })) : null), /*#__PURE__*/React.createElement("div", {
      className: "cx-group"
    }, /*#__PURE__*/React.createElement("span", {
      className: "cx-gh"
    }, "Finish"), /*#__PURE__*/React.createElement("label", {
      className: "cx-check"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: c.endCard,
      disabled: busy,
      onChange: e => this.cutSet({
        endCard: e.target.checked
      })
    }), "End card with your logo and a title"), c.endCard ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("label", {
      className: "cx-field"
    }, "Title", /*#__PURE__*/React.createElement("input", {
      value: c.endTitle,
      placeholder: "Title",
      onChange: e => this.cutSet({
        endTitle: e.target.value
      })
    })), /*#__PURE__*/React.createElement("label", {
      className: "cx-field"
    }, "Subtitle ", /*#__PURE__*/React.createElement("i", null, "optional"), /*#__PURE__*/React.createElement("input", {
      value: c.endSub,
      placeholder: "e.g. your site address",
      onChange: e => this.cutSet({
        endSub: e.target.value
      })
    }))) : null, /*#__PURE__*/React.createElement("label", {
      className: "cx-check"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: c.musicOn,
      disabled: busy,
      onChange: e => this.cutSet({
        musicOn: e.target.checked
      })
    }), "Background music"), c.musicOn ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("label", {
      className: "cx-field"
    }, "Music file link ", /*#__PURE__*/React.createElement("i", null, "mp3, loops quietly under the sound"), /*#__PURE__*/React.createElement("input", {
      value: c.music,
      placeholder: "https://\u2026",
      onChange: e => this.cutSet({
        music: e.target.value
      })
    })), /*#__PURE__*/React.createElement(MusicTracks, {
      value: c.music,
      disabled: busy,
      onChange: url => this.cutSet({
        music: url
      })
    })) : null), /*#__PURE__*/React.createElement("div", {
      className: "cx-group"
    }, /*#__PURE__*/React.createElement("span", {
      className: "cx-gh"
    }, "Output"), /*#__PURE__*/React.createElement("label", {
      className: "cx-field"
    }, "Shape", /*#__PURE__*/React.createElement("select", {
      value: c.aspect,
      disabled: busy,
      onChange: e => this.cutSet({
        aspect: e.target.value
      })
    }, ASPECTS.map(([k, l]) => /*#__PURE__*/React.createElement("option", {
      key: k,
      value: k
    }, l)))), /*#__PURE__*/React.createElement("span", {
      className: "cx-seg wide",
      role: "group",
      "aria-label": "One version or one per clip"
    }, [[false, "One cut"], [true, "One per clip"]].map(([v, l]) => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: String(v),
      "aria-pressed": c.separate === v,
      className: c.separate === v ? "on" : "",
      disabled: busy,
      onClick: () => this.cutSet({
        separate: v
      })
    }, l))), /*#__PURE__*/React.createElement("span", {
      className: "cx-note"
    }, c.separate ? "Each clip becomes its own version." : "Clips join with short fades into one version.")), this.foot(c.separate ? `Saves ${segs.length} version${segs.length === 1 ? "" : "s"}` : `Saves as v${this.nextVersion()} · ${fmt(total + (c.endCard ? 2.5 : 0))}`, busy && /cut/.test(s.busy) ? "Rendering…" : "Save the cut", this.saveCut, busy || !segs.length || c.loading));
  }
  renderMoments(a) {
    const s = this.state;
    const busy = !!s.busy;
    const m = s.moments;
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("label", {
      className: "cx-field"
    }, "For", /*#__PURE__*/React.createElement("select", {
      value: m.target,
      disabled: busy,
      onChange: e => this.momSet({
        target: e.target.value
      })
    }, TARGETS.map(([k, l]) => /*#__PURE__*/React.createElement("option", {
      key: k,
      value: k
    }, l)))), /*#__PURE__*/React.createElement("span", {
      className: "cx-note"
    }, "Shapes the length and the moments picked. ", m.transcript === null ? "Checking for a transcript…" : m.transcript ? "Uses the transcript too." : "No transcript: scene changes only."), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: m.clips === undefined ? "cx-go" : "cx-btn",
      disabled: busy,
      onClick: this.findMoments
    }, busy && /^Finding/.test(s.busy) ? "Finding…" : m.clips === undefined ? "Find moments" : "Find again"), m.clips && m.clips.length ? /*#__PURE__*/React.createElement("div", {
      className: "cx-group"
    }, /*#__PURE__*/React.createElement("span", {
      className: "cx-gh"
    }, "Suggested ", /*#__PURE__*/React.createElement("i", null, "\xB7 ", m.picked.size, " of ", m.clips.length, " picked")), m.clips.map((c, i) => /*#__PURE__*/React.createElement("div", {
      key: i,
      className: "cx-moment" + (m.picked.has(i) ? " on" : "")
    }, /*#__PURE__*/React.createElement("label", {
      className: "cx-check top"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: m.picked.has(i),
      disabled: busy,
      onChange: () => {
        const n = new Set(m.picked);
        if (n.has(i)) n.delete(i);else n.add(i);
        this.momSet({
          picked: n
        });
      }
    }), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, c.label), " ", /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, fmt(c.start), " to ", fmt(c.end), " \xB7 ", Math.round(c.end - c.start), " s"), c.why ? /*#__PURE__*/React.createElement("span", {
      className: "why"
    }, c.why) : null)), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "cx-btn sm",
      disabled: busy,
      onClick: () => {
        this.seek(c.start);
        const v = this.vid.current;
        if (v) v.play().catch(() => {});
      }
    }, "Play")))) : m.clips && !m.clips.length ? /*#__PURE__*/React.createElement("p", {
      className: "cx-note"
    }, "No moments were suggested. Try another purpose or find again.") : null, m.clips && m.clips.length ? this.foot(`Each picked moment is saved as a version`, busy && /^Cutting/.test(s.busy) ? s.busy : "Make clips", this.makeMoments, busy || !m.picked.size) : null);
  }
  renderCover(a, root) {
    const s = this.state;
    const busy = !!s.busy;
    const cv = s.cover;
    const d = this.durOf();
    const others = root && root.id !== a.id;
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "cx-cover-now"
    }, /*#__PURE__*/React.createElement("span", {
      className: "th"
    }, a.posterUrl ? /*#__PURE__*/React.createElement("img", {
      src: a.posterUrl,
      alt: "Current cover"
    }) : /*#__PURE__*/React.createElement("span", {
      className: "none"
    }, "No cover yet")), /*#__PURE__*/React.createElement("span", {
      className: "cx-note"
    }, a.posterUrl ? "The current cover." : "Without a cover, Assets and pickers show a blank or black frame.")), /*#__PURE__*/React.createElement("span", {
      className: "cx-seg wide",
      role: "group",
      "aria-label": "Cover source"
    }, [["frame", "A frame of the video"], ["image", "An image"]].map(([k, l]) => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: k,
      "aria-pressed": cv.mode === k,
      className: cv.mode === k ? "on" : "",
      disabled: busy,
      onClick: () => this.coverSet({
        mode: k
      })
    }, l))), cv.mode === "frame" ? /*#__PURE__*/React.createElement(React.Fragment, null, cv.tainted ? /*#__PURE__*/React.createElement("p", {
      className: "cx-note warn"
    }, "This video's frames cannot be read in the browser, so a frame cannot be saved. Use an image instead.") : /*#__PURE__*/React.createElement("p", {
      className: "cx-note"
    }, "Pause the video on the frame you want, or click a frame below to jump there."), s.frames && d ? /*#__PURE__*/React.createElement("div", {
      className: "cx-coverframes"
    }, s.frames.map((f, i) => {
      const t = Math.round((i + 0.5) * d / s.frames.length * 10) / 10;
      return /*#__PURE__*/React.createElement("button", {
        type: "button",
        key: i,
        title: `Jump to ${fmt(t)}`,
        "aria-label": `Jump to ${fmt(t)}`,
        disabled: busy,
        onClick: () => this.seek(t)
      }, /*#__PURE__*/React.createElement("img", {
        src: f,
        alt: ""
      }));
    })) : null, others ? /*#__PURE__*/React.createElement("label", {
      className: "cx-check"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: cv.alsoRoot,
      disabled: busy,
      onChange: e => this.coverSet({
        alsoRoot: e.target.checked
      })
    }), "Also use it on the original, which is what Assets shows") : null, this.foot(`Frame at ${fmt(s.time)}`, busy && /cover/.test(s.busy) ? "Saving…" : "Use this frame", this.saveCoverFrame, busy || cv.tainted || !a.url)) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("p", {
      className: "cx-note"
    }, "Pick an image from Assets or upload one."), /*#__PURE__*/React.createElement("div", {
      className: "cx-picker"
    }, /*#__PURE__*/React.createElement(AssetPicker, {
      compact: true,
      single: true,
      accept: "image",
      only: ["product", "image", "render"],
      picks: cv.imageId ? [cv.imageId] : [],
      disabled: busy,
      onAssets: list => this.coverSet({
        assets: list
      }),
      onChange: ids => this.coverSet({
        imageId: ids[0] || ""
      })
    })), others ? /*#__PURE__*/React.createElement("label", {
      className: "cx-check"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: cv.alsoRoot,
      disabled: busy,
      onChange: e => this.coverSet({
        alsoRoot: e.target.checked
      })
    }), "Also use it on the original, which is what Assets shows") : null, this.foot(cv.imageId ? "Image picked" : "No image picked yet", busy && /cover/.test(s.busy) ? "Saving…" : "Use this image", this.saveCoverImage, busy || !cv.imageId)));
  }
  renderRedirect(recipe, root) {
    const ClipForm = this.props.ClipForm;
    if (!recipe || !ClipForm) return /*#__PURE__*/React.createElement("p", {
      className: "cx-note"
    }, "This clip was not directed here, so there is nothing to re-direct.");
    return /*#__PURE__*/React.createElement("div", {
      className: "cx-redirect"
    }, /*#__PURE__*/React.createElement(ClipForm, {
      recipe: recipe,
      versionOf: root.id,
      onBusy: b => this.set({
        busy: b ? "Rendering…" : ""
      }),
      onDone: id => this.afterSave({
        id
      }),
      onCancel: this.closeTool
    }));
  }
  renderSizes(a) {
    const s = this.state;
    const busy = !!s.busy;
    const z = s.sizes;
    const n = z.picked.size;
    const toggle = k => {
      const picked = new Set(z.picked);
      if (picked.has(k)) picked.delete(k);else picked.add(k);
      this.sizeSet({
        picked
      });
    };
    const thumb = a.posterUrl || s.frames && s.frames[Math.floor(s.frames.length / 2)] || "";
    return /*#__PURE__*/React.createElement("div", {
      className: "cx-sizes"
    }, /*#__PURE__*/React.createElement("p", {
      className: "cx-lead"
    }, "Make this version in channel sizes. Each size is saved as a new version."), VIDEO_TARGETS.map(([k, label, w, h]) => /*#__PURE__*/React.createElement("label", {
      key: k,
      className: "cx-size" + (z.picked.has(k) ? " on" : "")
    }, /*#__PURE__*/React.createElement("span", {
      className: "th"
    }, /*#__PURE__*/React.createElement("span", {
      className: "fr",
      style: w >= h ? {
        width: 44,
        height: Math.round(44 * h / w)
      } : {
        height: 44,
        width: Math.round(44 * w / h)
      }
    }, thumb ? /*#__PURE__*/React.createElement("img", {
      src: thumb,
      alt: ""
    }) : null)), /*#__PURE__*/React.createElement("span", {
      className: "t"
    }, /*#__PURE__*/React.createElement("b", null, label), /*#__PURE__*/React.createElement("span", null, w, " \xD7 ", h)), /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: z.picked.has(k),
      disabled: busy,
      onChange: () => toggle(k),
      "aria-label": `Make ${label}`
    }))), /*#__PURE__*/React.createElement("div", {
      className: "cx-group"
    }, /*#__PURE__*/React.createElement("span", {
      className: "cx-gh"
    }, "Keep in frame"), /*#__PURE__*/React.createElement("span", {
      className: "cx-note"
    }, z.safe ? "Marked: that area stays in every size. Sizes that cannot fit it are padded instead of cropped." : "Optional: mark the area that must stay in every size, like the product or text."), /*#__PURE__*/React.createElement("span", {
      className: "cx-chips"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "cx-chip" + (z.drawing ? " on" : ""),
      disabled: busy,
      "aria-pressed": z.drawing,
      onClick: () => {
        const v = this.vid.current;
        if (v && !v.paused) v.pause();
        this.sizeSet({
          drawing: !z.drawing
        });
      }
    }, z.drawing ? "Drawing on the video…" : z.safe ? "Mark again" : "Mark it on the video"), z.safe ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "cx-chip",
      disabled: busy,
      onClick: () => this.sizeSet({
        safe: null
      })
    }, "Clear") : null)), /*#__PURE__*/React.createElement("div", {
      className: "cx-group"
    }, /*#__PURE__*/React.createElement("span", {
      className: "cx-gh"
    }, "Fill when it cannot crop"), /*#__PURE__*/React.createElement("span", {
      className: "cx-seg wide",
      role: "group",
      "aria-label": "Fill"
    }, [["blur", "Blurred video"], ["color", "Color"]].map(([k, l]) => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: k,
      "aria-pressed": z.fill === k,
      className: z.fill === k ? "on" : "",
      disabled: busy,
      onClick: () => this.sizeSet({
        fill: k
      })
    }, l))), z.fill === "color" ? /*#__PURE__*/React.createElement("label", {
      className: "cx-field row"
    }, "Color", /*#__PURE__*/React.createElement("input", {
      type: "color",
      value: z.fillColor,
      onChange: e => this.sizeSet({
        fillColor: e.target.value
      })
    })) : null), z.results && z.results.length ? /*#__PURE__*/React.createElement("div", {
      className: "cx-group"
    }, /*#__PURE__*/React.createElement("span", {
      className: "cx-gh"
    }, "Made"), z.results.map((x, i) => /*#__PURE__*/React.createElement("a", {
      key: i,
      className: "cx-made",
      href: x.url,
      target: "_blank",
      rel: "noreferrer"
    }, x.label, " \xB7 ", x.width, " \xD7 ", x.height, x.version ? ` · v${x.version.version || x.version.Version}` : ""))) : null, this.foot(`${n} size${n === 1 ? "" : "s"} · each saved as a version`, busy && /^Making/.test(s.busy) ? "Working…" : "Make sizes", this.makeSizes, busy || !n));
  }
  renderDetails(a, root, recipe) {
    const {
      hist,
      edits
    } = this.props;
    const s = this.state;
    const row = hist.find(h => h.versionId === a.id) || {};
    const job = (edits || []).find(j => idStr(j.outputAssetId || j.OutputAssetId) === a.id);
    const params = job ? (() => {
      try {
        const p = job.params || job.Params;
        return typeof p === "string" ? JSON.parse(p) : p;
      } catch (e) {
        return null;
      }
    })() : null;
    const by = String(row.by || "").split("@")[0];
    const dims = a.width && a.height ? `${a.width} × ${a.height}` : s.natural ? `${s.natural.w} × ${s.natural.h}` : "";
    const changed = [];
    if (job && (job.prompt || job.Prompt)) changed.push(["Asked for", job.prompt || job.Prompt]);
    if (params && params.start != null && params.end != null) changed.push(["Range", `${fmt(params.start)} to ${fmt(params.end)}`]);
    if (params && params.segments) changed.push(["Clips", String(params.segments)]);
    if (params && params.target) changed.push(["Size", String(params.target)]);
    if (params && params.aspect) changed.push(["Shape", String(params.aspect)]);
    if (params && params.captions != null && params.segments) changed.push(["Captions", params.captions ? "Burned in" : "None"]);
    if (params && params.endCard) changed.push(["End card", "Yes"]);
    return /*#__PURE__*/React.createElement("div", {
      className: "cx-details"
    }, /*#__PURE__*/React.createElement("div", {
      className: "cx-card kv"
    }, /*#__PURE__*/React.createElement("h6", null, "This version"), /*#__PURE__*/React.createElement("div", {
      className: "grid"
    }, /*#__PURE__*/React.createElement("span", null, "Version"), /*#__PURE__*/React.createElement("div", null, vlabel(a), a.id === root.id ? "" : ` of ${root.title || "the clip"}`), /*#__PURE__*/React.createElement("span", null, "Change"), /*#__PURE__*/React.createElement("div", null, row.label || (a.version <= 1 ? "Original" : "Saved version")), d0(a.durationSeconds || s.dur) ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", null, "Length"), /*#__PURE__*/React.createElement("div", null, fmt(a.durationSeconds || s.dur))) : null, dims ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", null, "Size"), /*#__PURE__*/React.createElement("div", null, dims)) : null, by ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", null, "By"), /*#__PURE__*/React.createElement("div", null, by)) : null, row.when || a.createdDate ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", null, "When"), /*#__PURE__*/React.createElement("div", null, fmtWhen(row.when || a.createdDate))) : null, a.title && a.title !== root.title ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", null, "Title"), /*#__PURE__*/React.createElement("div", null, a.title)) : null, /*#__PURE__*/React.createElement("span", null, "Cover"), /*#__PURE__*/React.createElement("div", null, a.posterUrl ? "Set" : "None yet", " ", /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "cx-link",
      onClick: () => this.pickTool("cover")
    }, a.posterUrl ? "Change" : "Pick one")), /*#__PURE__*/React.createElement("span", null, "File"), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("a", {
      href: a.url,
      target: "_blank",
      rel: "noreferrer"
    }, "Open in a new tab")))), changed.length ? /*#__PURE__*/React.createElement("div", {
      className: "cx-card kv"
    }, /*#__PURE__*/React.createElement("h6", null, "What changed"), /*#__PURE__*/React.createElement("div", {
      className: "grid"
    }, changed.map(([k, v]) => /*#__PURE__*/React.createElement(React.Fragment, {
      key: k
    }, /*#__PURE__*/React.createElement("span", null, k), /*#__PURE__*/React.createElement("div", null, v))))) : null, recipe ? /*#__PURE__*/React.createElement("div", {
      className: "cx-card kv"
    }, /*#__PURE__*/React.createElement("h6", null, "How it was directed"), /*#__PURE__*/React.createElement("div", {
      className: "grid"
    }, recipe.aspect ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", null, "Format"), /*#__PURE__*/React.createElement("div", null, recipe.aspect)) : null, recipe.lengthSeconds ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", null, "Length"), /*#__PURE__*/React.createElement("div", null, recipe.lengthSeconds, " s")) : null, recipe.imageUrl ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", null, "Starts from"), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("a", {
      href: recipe.imageUrl,
      target: "_blank",
      rel: "noreferrer"
    }, "the image"), (recipe.keepUrls || []).length ? ` · ${recipe.keepUrls.length} kept consistent` : "")) : null), (recipe.shots || []).length ? /*#__PURE__*/React.createElement("ol", {
      className: "cx-shots"
    }, recipe.shots.map((x, i) => /*#__PURE__*/React.createElement("li", {
      key: i
    }, x))) : null) : null, /*#__PURE__*/React.createElement("div", {
      className: "cx-card"
    }, /*#__PURE__*/React.createElement("h6", null, "History"), /*#__PURE__*/React.createElement("ul", {
      className: "cx-hist"
    }, hist.slice(0, 40).map(h => /*#__PURE__*/React.createElement("li", {
      key: h.key
    }, h.versionId ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: h.versionId === a.id ? "on" : "",
      "aria-pressed": h.versionId === a.id,
      title: `Show ${h.vl}`,
      onClick: () => this.props.onSelect(h.versionId)
    }, this.histInner(h)) : /*#__PURE__*/React.createElement("div", {
      className: "row"
    }, this.histInner(h)))))));
  }
  histInner(h) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      className: "v"
    }, h.vl ? /*#__PURE__*/React.createElement("span", {
      className: "plk-chip"
    }, h.vl) : null), /*#__PURE__*/React.createElement("span", {
      className: "t"
    }, /*#__PURE__*/React.createElement("b", null, h.label), h.detail ? /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, " \xB7 ", String(h.detail).slice(0, 90)) : null, h.status === "failed" ? /*#__PURE__*/React.createElement("span", {
      className: "plk-chip warn"
    }, "did not finish") : h.directed ? /*#__PURE__*/React.createElement("span", {
      className: "plk-chip"
    }, "directed here") : null, /*#__PURE__*/React.createElement("span", {
      className: "meta"
    }, [String(h.by || "").split("@")[0], fmtWhen(h.when)].filter(Boolean).join(" · "))));
  }
}
function d0(x) {
  return Number(x) > 0;
}
function fmtWhen(d) {
  if (!d) return "";
  const t = new Date(d);
  if (isNaN(t)) return "";
  const s = Math.max(0, (Date.now() - t.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return t.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric"
  });
}